import { getReviewedBasicCourseTopicCodes } from "./basicCourseTaskInfo";
import { COURSE_CODE_ALIASES, getMaturaCourseCode } from "./maturaCourseCodes";
import { isBasicCourseTaskType } from "./basicCourseSourcePolicy";
import { hasMaturaCoursePreparation } from "./preparation/maturaCoursePreparationAvailability";
import sections from "../sections";
import { MATURA_TASK_TYPE_CODE3, getMaturaTaskTypeCodes } from "./maturaTaskClassification";
import { getBasicCourseCatalog, orderBasicCourseTypes } from "./basicCourseMetadata";

export { getMaturaCourseCode };

const PROOF_SECTION_NO = 10;
const ADMIN_ONLY_COURSE_CODES = new Set(["10.2.1"]);
const COURSE_TOPIC_MINIMUM_TARGET = Object.freeze({
  // 50%: broad foundation made of recurring, relatively approachable skills.
  "1.1.2": 50,
  "1.1.5": 50,
  "1.2.4": 50,
  "1.4.3": 50,
  "1.5.2": 50,
  "2.1.2": 50,
  "2.7.2": 50,
  "2.2.5": 50,
  "2.3.1": 50,
  "2.3.2": 50,
  "3.1.2": 50,
  "3.2.1": 50,
  "4.1.1": 50,
  "4.2.1": 50,
  "5.1.2": 50,
  "5.2.3": 50,
  "6.1.3": 50,
  "6.1.9": 50,
  "6.3.2": 50,
  "7.2.3": 50,
  "7.3.1": 50,
  "8.3.1": 50,
  "8.3.2": 50,
  "9.1.1": 50,
  "9.2.1": 50,
  "9.3.2": 50,

  // 60%: the next useful layer after mastering the broad foundation.
  "2.2.3": 60,
  "3.3.3": 60,
  "4.3.1": 60,
  "6.5.3": 60,
  "7.1.2": 60,
  "8.1.3": 60,

  // 65%: less frequent skills that are useful after the 60% foundation.
  "6.1.7": 65,

  // 80%: techniques that are longer, less regular or require more fluency.
  "2.4.2": 80,
  "3.3.2": 80,
  "4.3.5": 80,
  "8.2.1": 80,
  "10.1.1": 80,

  // 100%: the most demanding quadratic material and geometry proofs.
  "3.3.6": 100,
  "10.2.1": 100,
});
export const getMaturaCourseProgressId = (code) => `matura_course_${String(code || "").replace(/\./g, "_")}`;
export const getMaturaCoursePreparationProgressId = (code) => `matura_course_preparation:${String(code || "")}`;
export const getMaturaCoursePracticeProgressId = (code) => `matura_course_practice_${String(code || "").replace(/\./g, "_")}`;
export const getMaturaCourseSectionProgressId = (sectionNo) => `matura_course_section_${Number(sectionNo)}`;
export const isMaturaCourseTopicVisible = (topic, role) => (
  role === "admin" || (
    !ADMIN_ONLY_COURSE_CODES.has(String(topic?.code || ""))
    && hasMaturaCoursePreparation(topic?.code)
  )
);


// The exam course groups tasks by the skill needed to solve them. This may be
// narrower than the canonical school classification or the source-sheet tags.
const COURSE_TASK_TYPE_CODES = Object.freeze({
  MaturaBasic_PolynomialProductRealRootCount:["2.2.5"],
  MaturaPowerDivisibilityTruthClosedTask: ["10.1.1"],
  "MaturaBasic_negative-odd-roots-sum": ["1.1.5"],
  MaturaBasic_OddRootQuotient: ["1.1.5"],
  MaturaBasic_CubeRootsProductQuotient: ["1.1.5"],
  MaturaBasic_CubeRootNegativeFractionProduct: ["1.1.5"],
  MaturaBasic_RadicalDifferenceAsFractionalPower: ["1.1.5"],
  MaturaCirclePointEquationTruthClosedTask: ["7.3.1"],
  MaturaArithGeoSignsClosedTask: ["4.3.5"],
  MaturaArithmeticSumClosedTask: ["4.2.1"],
  MaturaCuboidGeoSequenceVolumeOpenTask: ["8.1.3"],
  MaturaLinearSequenceGeometricTripletOpenTask: ["4.3.5"],
  MaturaQuadraticSequencePFClosedTask: ["4.1.1", "4.2.1"],
  MaturaRecursiveSequencePFClosedTask: ["4.1.1", "4.3.5"],
  MaturaRecursiveSequenceTermClosedTask: ["4.1.1"],
  MaturaTrapezoidAreasClosedTask: ["6.5.3"],
  MaturaTrigTanFromCosClosedTask: ["5.1.2", "5.2.3"],
});

// A section test may use a narrower representative pool than regular practice.
// Shared task types stay available in practice without replacing the core skill
// selected for the section summary.
const COURSE_SECTION_TEST_TASK_TYPES = Object.freeze({
  "1.5.2": [
    ...new Set([...getBasicCourseCatalog().keys()].filter(type =>
      getReviewedBasicCourseTopicCodes(type)?.[0] === "1.5.2"
    )),
  ],
  "2.1.2": [
    "MaturaBasic_CubicCommonFactorSquare",
    "MaturaDifferenceSquaresClosedTask",
  ],
});



const COURSE_TOPIC_OVERRIDES = Object.freeze({
  "2.1.2": {title: "Wzory skróconego mnożenia", topicName: "Wyrażenia algebraiczne"},
  "2.7.2": {title: "Działania na wielomianach", topicName: "Wielomiany"},
  "10.1.1": { title: "Podzielność i dowody podzielności", topicName: "Podzielność liczb całkowitych" },
  "1.1.2": {
    title: "Wartość bezwzględna",
    topicName: "Zbiory, wartość bezwzględna i przedziały",
  },
  "1.2.4": {
    title: "Procenty",
    topicName: "Procenty",
  },
  "1.4.3": {
    title: "Potęgi",
    topicName: "Potęgi i pierwiastki",
  },
  "1.5.2": {
    title: "Logarytmy",
    topicName: "Logarytmy",
  },
  "2.2.3": {
    title: "Wyrażenia i równania wymierne",
    topicName: "Równania",
    preparationLessonId: "2_1_3",
  },
  "2.4.2": {
    title: "Układy równań liniowych",
    topicName: "Układy równań",
  },
  "3.2.1": {
    title: "Funkcja liniowa",
    topicName: "Funkcja liniowa",
  },
  "3.3.2": {
    title: "Postać kanoniczna",
    topicName: "Funkcja kwadratowa",
    groupTitle: "Funkcja kwadratowa",
  },
  "3.3.3": {
    title: "Postać iloczynowa",
    topicName: "Funkcja kwadratowa",
    groupTitle: "Funkcja kwadratowa",
  },
  "3.3.6": {
    title: "Optymalizacja",
    topicName: "Funkcja kwadratowa",
    groupTitle: "Funkcja kwadratowa",
  },
  "4.1.1": {
    title: "Definicja ciągu",
    topicName: "Podstawy ciągów",
  },
  "4.2.1": {
    title: "Ciąg arytmetyczny",
    topicName: "Ciąg arytmetyczny",
  },
  "4.3.1": {
    title: "Ciąg geometryczny",
    topicName: "Ciąg geometryczny",
  },
  "4.3.5": {
    title: "Ciągi arytmetyczne i geometryczne",
    topicName: "Ciągi mieszane",
    preparationLessonId: "4_3_4",
  },
  "5.1.2": {
    title: "Trygonometria kąta ostrego",
    topicName: "Trygonometria kąta ostrego",
  },
  "5.2.3": {
    title: "Tożsamości trygonometryczne",
    topicName: "Tożsamości trygonometryczne",
    preparationLessonId: "5_1_4",
  },
  "6.1.9": {
    title: "Pole trójkąta i twierdzenie cosinusów",
    topicName: "Trójkąty",
    preparationLessonId: "6_1_2",
  },
  "6.1.7": {
    title: "Podobieństwo trójkątów",
    topicName: "Trójkąty podobne",
  },
  "6.3.2": {
    title: "Kąty w okręgu",
    topicName: "Okręgi i koła",
  },
  "6.5.3": {
    title: "Skala podobieństwa",
    topicName: "Skala i podobieństwo",
  },
  "7.3.1": {
    title: "Równanie okręgu",
    topicName: "Równanie okręgu",
  },
  "7.1.2": {
    title: "Zastosowania geometrii analitycznej",
    topicName: "Zastosowania geometrii analitycznej",
  },
  "8.1.3": {
    title: "Graniastosłupy i ostrosłupy",
    topicName: "Graniastosłupy i ostrosłupy",
  },
  "8.3.1": {
    title: "Walce i stożki",
    topicName: "Bryły obrotowe",
  },
  "9.1.1": {
    title: "Kombinatoryka",
    topicName: "Kombinatoryka",
  },
  "9.3.2": {
    title: "Średnia, mediana i dominanta",
    topicName: "Statystyka opisowa",
  },
  "10.2.1": {
    title: "Dowody dotyczące figur",
    topicName: "Dowody geometryczne",
    adminNote: "Robocze · odłożone do późniejszego etapu",
  },
});

function compareCodes(left, right) {
  const a = left.split(".").map(Number);
  const b = right.split(".").map(Number);
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const difference = (a[index] || 0) - (b[index] || 0);
    if (difference) return difference;
  }
  return 0;
}

function buildLessonIndex() {
  const index = new Map();

  sections.forEach((section, sectionIndex) => {
    let globalSubtopicIndex = 0;
    (section.topics || []).forEach((topic) => {
      (topic.subtopics || []).forEach((subtopic) => {
        if (subtopic?.id) {
          index.set(String(subtopic.id), {
            name: subtopic.name,
            topicName: topic.name,
            sectionIndex,
            globalSubtopicIndex,
            lessonId: String(subtopic.id),
            isQuiz: subtopic.type === "quiz",
          });
        }
        globalSubtopicIndex += 1;
      });
    });
  });

  return index;
}

const lessonIndex = buildLessonIndex();

function cleanSectionTitle(title = "") {
  return title.replace(/^\s*[IVXLCDM]+\.\s*/i, "").trim();
}

function fallbackTopicName(code) {
  return `Podtemat ${code}`;
}

export function buildMaturaCourseStructure() {
  const taskTypesByCode = new Map();

  const basicCatalog = getBasicCourseCatalog();
  const allTypes = new Set([...Object.keys(MATURA_TASK_TYPE_CODE3), ...basicCatalog.keys()]);
  allTypes.forEach((taskType) => {
    if (!isBasicCourseTaskType(taskType)) return;
    const courseCodes = getReviewedBasicCourseTopicCodes(taskType) || COURSE_TASK_TYPE_CODES[taskType]
      || (basicCatalog.get(taskType)?.codes || getMaturaTaskTypeCodes(taskType));
    courseCodes.forEach((sourceCode) => {
      const code = getMaturaCourseCode(sourceCode);
      if (!taskTypesByCode.has(code)) {
        taskTypesByCode.set(code, { sourceCodes: new Set(), taskTypes: new Set() });
      }
      taskTypesByCode.get(code).sourceCodes.add(sourceCode);
      taskTypesByCode.get(code).taskTypes.add(taskType);
    });
  });

  const groups = new Map();
  Array.from(taskTypesByCode.keys()).sort(compareCodes).forEach((code) => {
    const [sectionNo] = code.split(".").map(Number);
    const section = sections[sectionNo - 1];
    if (!section) return;

    const courseTypes = taskTypesByCode.get(code);
    const aliasedSourceCodes = Object.entries(COURSE_CODE_ALIASES)
      .filter(([, courseCode]) => courseCode === code)
      .map(([sourceCode]) => sourceCode);
    const override = COURSE_TOPIC_OVERRIDES[code] || null;
    const lessonId = override?.preparationLessonId || code.replace(/\./g, "_");
    const lesson = lessonIndex.get(lessonId) || null;
    const item = {
      code,
      sectionNo,
      title: override?.title || lesson?.name || fallbackTopicName(code),
      topicName: override?.topicName || lesson?.topicName || "Zagadnienie maturalne",
      groupTitle: override?.groupTitle || null,
      adminNote: override?.adminNote || null,
      minimumTarget: COURSE_TOPIC_MINIMUM_TARGET[code] || 100,
      sourceCodes: Array.from(new Set([
        ...courseTypes.sourceCodes,
        ...aliasedSourceCodes,
      ])).sort(compareCodes),
      taskTypes: orderBasicCourseTypes(Array.from(courseTypes.taskTypes)),
      sectionTestTaskTypes: COURSE_SECTION_TEST_TASK_TYPES[code]?.filter(isBasicCourseTaskType) || null,
      preparationLessonId: lesson?.lessonId || null,
      preparationPath: lesson
        ? `/lekcja/${lesson.sectionIndex}/${lesson.globalSubtopicIndex}`
        : null,
    };

    if (!groups.has(sectionNo)) {
      groups.set(sectionNo, {
        sectionNo,
        title: cleanSectionTitle(section.title),
        topics: [],
      });
    }
    groups.get(sectionNo).topics.push(item);
  });

  const ordered = Array.from(groups.values()).sort((a, b) => a.sectionNo - b.sectionNo);
  const proofs = ordered.find((group) => group.sectionNo === PROOF_SECTION_NO);
  return proofs ? [...ordered.filter((group) => group !== proofs), proofs] : ordered;
}

export const MATURA_COURSE_STRUCTURE = Object.freeze(buildMaturaCourseStructure());
